import hmd_softlock_logo from 'url:/assets/hmd_softlock_logo.svg'
import { Form, Formik } from 'formik'
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import * as Yup from 'yup'
import { useApiLogin } from '../../api/account/apiUsers'
import { Button } from '../../genericComponents/Button'
import { InputPasswordLogin } from '../../inputs/InputPasswordLogin'
import { InputUsernameLogin } from '../../inputs/InputUsernameLogin'
import { ApiError, LoginDto } from '../../generated'
import { INPUT_FORM_COMMON_SIZE } from '../../genericComponents/OverlayWrapper'
import { FlexContainer } from '../../genericComponents/layout/FlexContainer'

export type LoginFormDto = {
  username: string
  password: string
}

export type VerifyFormDto = {
  code: number
  submit: string
}

export const redirectPages: Record<string, string> = {
  SYSTEM_ADMIN: 'system/devices',
  TFA_REQUIRED: '/verify',
  REGISTRATION_NEEDED: '/ftu',
  MAINTENANCE_NEEDED: '/ftu',
  PROGRAM_ADMIN: 'program/devices',
  PROGRAM_MANAGER: 'program/devices',
  PROGRAM_CONTROLLER: 'program/devices',
  PROGRAM_MANAGER_ADMIN: 'program/devices',
  SERVICE_TECHNICIAN: 'program/devices',
  ENROLL_TECH: 'program/devices',
  WAREHOUSE_TECH: 'program/devices',
  CARE_ADMIN: 'care-admin/devices',
}

export const LoginPage: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const navigate = useNavigate()
  const validationSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    password: Yup.string().required('Password is required'),
  })

  const { mutateAsync: login } = useApiLogin()
  const handleSubmit = async (
    values: { username: string; password: string },
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    await login(
      {
        username: values.username,
        password: values.password,
      },
      {
        onError: ((data: { body: ApiError }) => {
          setErrorMessage(
            data.body.message || `Sorry, the credentials you've entered are incorrect.`
          )
          setSubmitting(false)
        }) as (error: unknown, variables: LoginFormDto, context: unknown) => void,
        onSuccess: ((data: LoginDto) => {
          if (data.role == 'REGISTRATION_NEEDED') {
            void navigate('/ftu')
          } else if (data.role == 'TFA_REQUIRED') {
            void navigate('/verify')
          } else {
            if (data.role) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              window.location.href = redirectPages[data.role] ?? ''
            }
          }
        }) as (data: unknown, variables: LoginFormDto, context: unknown) => void,
      }
    )
  }

  return (
    <div className="flex h-screen justify-center m-0 text-primaryFontColor text-sm font-manrope-verdana">
      <div className="bg-brandDark w-[40%] h-full bg-cover bg-login-art">
        <div className="w-full h-full bg-primaryAccentColor opacity-60"></div>
      </div>
      <div className="flex bg-white w-[60%] justify-center">
        <FlexContainer
          align="items-start"
          justify="justify-center"
          className={'flex-col ' + INPUT_FORM_COMMON_SIZE}
        >
          <img src={hmd_softlock_logo} alt="Logo" className="mb-6 w-[200px]" />
          <p className="my-3">Enter your credentials to sign in</p>
          <Formik
            initialValues={{ username: '', password: '' }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => handleSubmit(values, setSubmitting)}
          >
            {({ isSubmitting }) => (
              <Form className={'flex flex-col ' + INPUT_FORM_COMMON_SIZE}>
                <InputUsernameLogin name="username" placeholder="Username" className="w-full" />

                <div className="flex flex-col">
                  <InputPasswordLogin
                    name="password"
                    type="password"
                    placeholder="Password"
                    className="w-full"
                  />
                </div>
                {errorMessage && (
                  <div className="flex">
                    <span className="text-alertColor text-sm font-semibold italic">
                      {errorMessage}
                    </span>
                  </div>
                )}
                <Link
                  to="/forgot_pw"
                  className="text-linkColor text-sm self-center mt-6 mb-8 underline hover:text-blue-600"
                >
                  Forgot Password?
                </Link>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="min-h-10 self-center max-w-64 w-full"
                >
                  Sign In
                </Button>
              </Form>
            )}
          </Formik>
        </FlexContainer>
      </div>
    </div>
  )
}
